import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import contentSectionService from '../../services/contentSectionService';
import imageService from '../../services/imageService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import MessageComponent from '../../components/Common/MessageComponent';
import FilterEditor from './FilterEditor';
import ProductPicker from './ProductPicker';
import PreviewGrid from './PreviewGrid';
import {
  AUTO_SORTS, LAYOUTS, LIMIT_PRESETS, SECTION_TYPE, SECTION_TYPES, ZONE, ZONES,
  extractErrors, sectionTypeMeta, toLocalInput, toUtcIso,
} from './constants';

const emptyForm = () => ({
  slug: '',
  titleEn: '', titleAr: '', titleKu: '',
  subtitleEn: '', subtitleAr: '', subtitleKu: '',
  bannerUrl: '',
  sectionType: SECTION_TYPE.BRAND,
  layoutStyle: 1, // carousel is the storefront's standard look
  isActive: true,
  homepageLimit: 12,
  showViewAll: true,
  autoFill: true,
  autoSort: 1,
  startsAt: '', endsAt: '',
  filters: sectionTypeMeta(SECTION_TYPE.BRAND).preset.map((f) => ({ ...f })),
  pinned: [],
  zone: ZONE.AFTER_HERO,
  websiteVisible: true,
  sortOrder: null,
});

const fromDetail = (d) => ({
  slug: d.slug || '',
  titleEn: d.titleEn || '', titleAr: d.titleAr || '', titleKu: d.titleKu || '',
  subtitleEn: d.subtitleEn || '', subtitleAr: d.subtitleAr || '', subtitleKu: d.subtitleKu || '',
  bannerUrl: d.bannerUrl || '',
  sectionType: d.sectionType,
  layoutStyle: d.layoutStyle,
  isActive: d.isActive,
  homepageLimit: d.homepageLimit,
  showViewAll: d.showViewAll,
  autoFill: d.autoFill,
  autoSort: d.autoSort,
  startsAt: toLocalInput(d.startsAtUtc), endsAt: toLocalInput(d.endsAtUtc),
  filters: (d.filters || []).map((f) => ({ type: f.type, value: String(f.value) })),
  pinned: (d.pinnedProducts || []).map((p) => ({ ...p })),
  zone: d.placement?.zone ?? ZONE.AFTER_HERO,
  websiteVisible: d.placement?.websiteVisible ?? true,
  sortOrder: null, // unchanged on save; reorder from the list page
});

const buildRequest = (f) => ({
  slug: f.slug.trim() || null,
  titleEn: f.titleEn, titleAr: f.titleAr, titleKu: f.titleKu || null,
  subtitleEn: f.subtitleEn || null, subtitleAr: f.subtitleAr || null, subtitleKu: f.subtitleKu || null,
  bannerUrl: f.bannerUrl || null,
  sectionType: f.sectionType,
  layoutStyle: f.layoutStyle,
  isActive: f.isActive,
  homepageLimit: Number(f.homepageLimit) || 0,
  showViewAll: f.showViewAll,
  autoFill: f.sectionType === SECTION_TYPE.CUSTOM ? false : f.autoFill,
  autoSort: f.autoSort,
  startsAtUtc: toUtcIso(f.startsAt),
  endsAtUtc: toUtcIso(f.endsAt),
  filters: f.sectionType === SECTION_TYPE.CUSTOM
    ? []
    : f.filters.filter((x) => String(x.value).trim() !== '').map((x) => ({ type: x.type, value: String(x.value).trim() })),
  pinnedProductIds: f.pinned.map((p) => p.productId),
  placement: { zone: f.zone, sortOrder: f.sortOrder, websiteVisible: f.websiteVisible },
});

const Group = ({ title, hint, children, errors }) => (
  <section className="card cs-group">
    <div className="cs-group-head">
      <h2>{title}</h2>
      {hint ? <p>{hint}</p> : null}
    </div>
    {errors && errors.length > 0 ? (
      <ul className="cs-errors">
        {errors.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    ) : null}
    <div className="cs-group-body">{children}</div>
  </section>
);

const ContentSectionEdit = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { isRtl } = useLocalization();
  const tr = (en, ar) => (isRtl ? ar : en);

  const [form, setForm] = useState(emptyForm);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({ fields: {}, messages: [] });
  const [toast, setToast] = useState({ visible: false, type: 'success', message: '' });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const previewSeq = useRef(0);

  const set = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  // initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [opts, detail] = await Promise.all([
          contentSectionService.options(),
          isNew ? Promise.resolve(null) : contentSectionService.get(id),
        ]);
        if (cancelled) return;
        setOptions(opts);
        if (detail) setForm(fromDetail(detail));
      } catch (e) {
        if (!cancelled) setErrors(extractErrors(e, tr('Failed to load the section.', 'تعذر تحميل القسم.')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // live preview of the unsaved draft (debounced): pinned + auto-filled, exactly as the website would build it
  const draftKey = useMemo(() => JSON.stringify(buildRequest(form)), [form]);
  useEffect(() => {
    if (loading) return undefined;
    const mine = ++previewSeq.current;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const p = await contentSectionService.preview(JSON.parse(draftKey), isNew ? undefined : id);
        if (mine === previewSeq.current) setPreview(p);
      } catch {
        if (mine === previewSeq.current) setPreview(null);
      } finally {
        if (mine === previewSeq.current) setPreviewing(false);
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, loading]);

  const isCustom = form.sectionType === SECTION_TYPE.CUSTOM;
  const typeMeta = sectionTypeMeta(form.sectionType);

  const changeType = (value) => {
    const meta = sectionTypeMeta(value);
    setForm((prev) => ({
      ...prev,
      sectionType: value,
      filters: meta.preset.map((f) => ({ ...f })),
      autoFill: value === SECTION_TYPE.CUSTOM ? false : prev.sectionType === SECTION_TYPE.CUSTOM ? true : prev.autoFill,
      // pinned products must satisfy the new conditions; the server re-validates, the picker re-offers eligible ones
      pinned: value === SECTION_TYPE.CUSTOM ? prev.pinned : prev.pinned.map((p) => ({ ...p, isEligible: undefined })),
    }));
    setDirty(true);
  };

  const onBanner = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const res = await imageService.uploadImages([file], 'sections');
      const url = res?.imageUrls?.[0];
      if (url) set({ bannerUrl: url });
    } catch (e) {
      setErrors({ fields: {}, messages: [e.message] });
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setErrors({ fields: {}, messages: [] });
    try {
      const body = buildRequest(form);
      const saved = isNew ? await contentSectionService.create(body) : await contentSectionService.update(id, body);
      setDirty(false);
      setToast({ visible: true, type: 'success', message: tr('Section saved. The website updates within a minute.', 'تم حفظ القسم. يظهر على الموقع خلال دقيقة.') });
      if (isNew) navigate(`/content-sections/${saved.id}`, { replace: true });
      else setForm(fromDetail(saved));
    } catch (err) {
      setErrors(extractErrors(err, tr('Failed to save the section.', 'تعذر حفظ القسم.')));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading text={tr('Loading section...', 'جاري تحميل القسم...')} />;

  const f = errors.fields;
  const bannerSrc = getProductImageUrl(form.bannerUrl);
  const slugPreview = form.slug || tr('(generated from the English title)', '(يُنشأ من العنوان الإنجليزي)');

  return (
    <div className="cs-page">
      <MessageComponent
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast((t) => ({ ...t, visible: false }))}
      />

      <div className="page-header">
        <div className="page-header-main">
          <Link to="/content-sections" className="cs-back">{isRtl ? '→' : '←'} {tr('Content Sections', 'أقسام المحتوى')}</Link>
          <h1 className="page-title">{isNew ? tr('New section', 'قسم جديد') : form.titleEn || tr('Edit section', 'تعديل القسم')}</h1>
          <p className="page-description">
            {tr(
              'A section chooses and orders products. Prices, discounts and shipping always come from the Discounts system.',
              'القسم يختار المنتجات ويرتّبها فقط. الأسعار والخصومات والشحن تأتي دائماً من نظام الخصومات.',
            )}
          </p>
        </div>
        <div className="page-header-actions">
          <Button variant="secondary" onClick={() => navigate('/content-sections')}>{tr('Cancel', 'إلغاء')}</Button>
          <Button onClick={save} disabled={saving || uploading}>
            {saving ? tr('Saving…', 'جارٍ الحفظ…') : tr('Save section', 'حفظ القسم')}
          </Button>
        </div>
      </div>

      {errors.messages.length > 0 ? (
        <div className="cs-alert" role="alert">
          <strong>{tr('Please fix the following:', 'يرجى تصحيح ما يلي:')}</strong>
          <ul>
            {errors.messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <form className="cs-layout" onSubmit={save}>
        <div className="cs-main">
          {/* ───────── General ───────── */}
          <Group title={tr('1 · General information', '١ · المعلومات العامة')}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Title (English)', 'العنوان (إنجليزي)')} <span className="text-danger">*</span></label>
                <input className={`form-input ${f.titleEn ? 'error' : ''}`} value={form.titleEn} maxLength={200} onChange={(e) => set({ titleEn: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Title (Arabic)', 'العنوان (عربي)')} <span className="text-danger">*</span></label>
                <input className={`form-input ${f.titleAr ? 'error' : ''}`} dir="rtl" value={form.titleAr} maxLength={200} onChange={(e) => set({ titleAr: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Title (Kurdish)', 'العنوان (كردي)')}</label>
                <input className="form-input" dir="rtl" value={form.titleKu} maxLength={200} onChange={(e) => set({ titleKu: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Subtitle (English)', 'العنوان الفرعي (إنجليزي)')}</label>
                <input className="form-input" value={form.subtitleEn} maxLength={400} onChange={(e) => set({ subtitleEn: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Subtitle (Arabic)', 'العنوان الفرعي (عربي)')}</label>
                <input className="form-input" dir="rtl" value={form.subtitleAr} maxLength={400} onChange={(e) => set({ subtitleAr: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Subtitle (Kurdish)', 'العنوان الفرعي (كردي)')}</label>
                <input className="form-input" dir="rtl" value={form.subtitleKu} maxLength={400} onChange={(e) => set({ subtitleKu: e.target.value })} />
              </div>
            </div>
            <p className="cs-hint">
              {tr(
                'The website currently speaks Arabic and English. Kurdish text is saved for when that language is added; until then the store falls back to the other titles.',
                'الموقع يدعم حالياً العربية والإنجليزية. يُحفظ النص الكردي لحين إضافة اللغة، وحتى ذلك الحين يُستخدم العنوان الآخر.',
              )}
            </p>

            <div className="form-group">
              <label className="form-label">{tr('Section type', 'نوع القسم')}</label>
              <div className="cs-type-grid">
                {SECTION_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    className={`cs-type ${form.sectionType === t.value ? 'is-active' : ''}`}
                    onClick={() => changeType(t.value)}
                  >
                    <strong>{isRtl ? t.ar : t.en}</strong>
                    <span>{isRtl ? t.descAr : t.descEn}</span>
                  </button>
                ))}
              </div>
            </div>

            <label className="form-checkbox">
              <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
              <span className="form-checkbox-label">{tr('Active (turn off to disable the section everywhere)', 'مفعّل (أوقفه لتعطيل القسم في كل مكان)')}</span>
            </label>

            <details className="cs-advanced">
              <summary>{tr('URL slug (advanced)', 'معرّف الرابط (متقدم)')}</summary>
              <div className="form-group">
                <input className={`form-input ${f.slug ? 'error' : ''}`} dir="ltr" value={form.slug} placeholder="assaf-perfumes" onChange={(e) => set({ slug: e.target.value.toLowerCase() })} />
                <div className="form-hint" dir="ltr">/catalog?section={slugPreview}</div>
                <div className="form-hint">{tr('Changing the slug breaks links customers already have.', 'تغيير المعرّف يعطّل الروابط المحفوظة لدى العملاء.')}</div>
              </div>
            </details>
          </Group>

          {/* ───────── Product selection ───────── */}
          <Group
            title={tr('2 · Product selection', '٢ · اختيار المنتجات')}
            hint={isCustom
              ? tr('Custom sections contain exactly the products you pick, in your order. Nothing is added automatically.', 'القسم المخصص يحتوي المنتجات التي تختارها بالضبط وبترتيبك. لا يُضاف شيء تلقائياً.')
              : tr('Products matching ALL condition types qualify. Pinned products come first, then the rest are added automatically.', 'المنتجات المطابقة لجميع الشروط تتأهل. المثبّتة أولاً ثم تُضاف البقية تلقائياً.')}
            errors={[...(f.filters || []), ...(f.pinnedProductIds || [])]}
          >
            {!isCustom ? (
              <div className="form-group">
                <label className="form-label">{tr('Conditions', 'الشروط')}</label>
                <FilterEditor
                  filters={form.filters}
                  onChange={(filters) => set({ filters })}
                  options={options}
                  sectionType={form.sectionType}
                  isRtl={isRtl}
                />
              </div>
            ) : null}

            <div className="form-group">
              <label className="form-label">{tr('Pinned products', 'المنتجات المثبّتة')}</label>
              <ProductPicker
                sectionType={form.sectionType}
                filters={form.filters}
                pinned={form.pinned}
                onChange={(pinned) => set({ pinned })}
                isRtl={isRtl}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className={`form-checkbox ${isCustom ? 'is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!isCustom && form.autoFill}
                    disabled={isCustom}
                    onChange={(e) => set({ autoFill: e.target.checked })}
                  />
                  <span className="form-checkbox-label">
                    {tr('Automatically add matching products', 'إضافة المنتجات المطابقة تلقائياً')}
                  </span>
                </label>
                <div className="form-hint">
                  {isCustom
                    ? tr('Always off for custom sections.', 'دائماً متوقف للأقسام المخصصة.')
                    : form.autoFill
                      ? tr('Fills the remaining places up to the homepage limit; "View all" shows every match.', 'يملأ الأماكن المتبقية حتى حد الصفحة الرئيسية، و«عرض الكل» يعرض كل المطابقات.')
                      : tr('Only pinned products appear (homepage and "View all").', 'تظهر المنتجات المثبّتة فقط (في الرئيسية و«عرض الكل»).')}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Automatic sorting', 'الفرز التلقائي')}</label>
                <select className="form-select" value={form.autoSort} disabled={isCustom || !form.autoFill} onChange={(e) => set({ autoSort: Number(e.target.value) })}>
                  {AUTO_SORTS.map((s) => (
                    <option key={s.value} value={s.value}>{isRtl ? s.ar : s.en}</option>
                  ))}
                </select>
                <div className="form-hint">{tr('Pinned products always stay first.', 'المنتجات المثبّتة تبقى دائماً في المقدمة.')}</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{tr('Homepage product limit', 'عدد المنتجات في الصفحة الرئيسية')} <span className="text-danger">*</span></label>
              <div className="cs-limit">
                {LIMIT_PRESETS.map((n) => (
                  <button type="button" key={n} className={`cs-chip ${Number(form.homepageLimit) === n ? 'is-active' : ''}`} onClick={() => set({ homepageLimit: n })}>{n}</button>
                ))}
                <input className={`form-input cs-limit-input ${f.homepageLimit ? 'error' : ''}`} type="number" min="1" max="60" value={form.homepageLimit} onChange={(e) => set({ homepageLimit: e.target.value })} />
              </div>
              <div className="form-hint">{tr('Only limits the homepage. "View all" always lists the complete section.', 'يحدّد الصفحة الرئيسية فقط. «عرض الكل» يعرض القسم كاملاً دائماً.')}</div>
            </div>
          </Group>

          {/* ───────── Display ───────── */}
          <Group title={tr('3 · Display settings', '٣ · إعدادات العرض')}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Layout style', 'نمط العرض')}</label>
                <select className="form-select" value={form.layoutStyle} onChange={(e) => set({ layoutStyle: Number(e.target.value) })}>
                  {LAYOUTS.map((l) => (
                    <option key={l.value} value={l.value}>{isRtl ? l.ar : l.en}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Homepage position', 'موضع الصفحة الرئيسية')}</label>
                <select className="form-select" value={form.zone} onChange={(e) => set({ zone: Number(e.target.value), sortOrder: null })}>
                  {ZONES.map((z) => (
                    <option key={z.value} value={z.value}>{isRtl ? z.ar : z.en}</option>
                  ))}
                </select>
                <div className="form-hint">
                  {isNew || form.sortOrder === null
                    ? tr('New or moved sections go to the end of the zone. Drag sections on the list page to reorder.', 'الأقسام الجديدة أو المنقولة تذهب لنهاية الموضع. اسحب الأقسام في صفحة القائمة لإعادة الترتيب.')
                    : null}
                </div>
              </div>
            </div>

            {form.layoutStyle === 3 ? (
              <div className="form-group">
                <label className="form-label">{tr('Section banner', 'بانر القسم')}</label>
                <div className="cs-banner">
                  {bannerSrc ? <img src={bannerSrc} alt="" /> : <span>{tr('No banner', 'لا يوجد بانر')}</span>}
                </div>
                <div className="cs-banner-actions">
                  <label className="btn btn-secondary btn-small">
                    {uploading ? tr('Uploading…', 'جارٍ الرفع…') : tr('Upload banner', 'رفع بانر')}
                    <input type="file" accept="image/*" hidden onChange={(e) => onBanner(e.target.files?.[0])} />
                  </label>
                  {form.bannerUrl ? (
                    <button type="button" className="btn btn-ghost btn-small" onClick={() => set({ bannerUrl: '' })}>{tr('Remove', 'إزالة')}</button>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="cs-switches">
              <label className="form-checkbox">
                <input type="checkbox" checked={form.showViewAll} onChange={(e) => set({ showViewAll: e.target.checked })} />
                <span className="form-checkbox-label">{tr('Show the "View all" button', 'إظهار زر «عرض الكل»')}</span>
              </label>
              <label className="form-checkbox">
                <input type="checkbox" checked={form.websiteVisible} onChange={(e) => set({ websiteVisible: e.target.checked })} />
                <span className="form-checkbox-label">{tr('Visible on the website', 'ظاهر في الموقع')}</span>
              </label>
            </div>
          </Group>

          {/* ───────── Scheduling ───────── */}
          <Group
            title={tr('4 · Scheduling', '٤ · الجدولة')}
            hint={tr(
              'Leave empty for "always active". Times are in your local timezone and stored as UTC. A promotion has its own, independent schedule on the Discounts page.',
              'اتركها فارغة ليكون القسم دائماً فعّالاً. الأوقات بتوقيتك المحلي وتُحفظ بالتوقيت العالمي. للعرض الترويجي جدول مستقل في صفحة الخصومات.',
            )}
            errors={[...(f.endsAtUtc || []), ...(f.startsAtUtc || [])]}
          >
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Starts', 'يبدأ')}</label>
                <input className="form-input" type="datetime-local" value={form.startsAt} onChange={(e) => set({ startsAt: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Ends', 'ينتهي')}</label>
                <input className={`form-input ${f.endsAtUtc ? 'error' : ''}`} type="datetime-local" value={form.endsAt} onChange={(e) => set({ endsAt: e.target.value })} />
              </div>
            </div>
            {(form.startsAt || form.endsAt) ? (
              <button type="button" className="btn btn-ghost btn-small" onClick={() => set({ startsAt: '', endsAt: '' })}>{tr('Always active', 'دائماً فعّال')}</button>
            ) : null}
          </Group>
        </div>

        {/* ───────── Preview ───────── */}
        <aside className="cs-side">
          <div className="card cs-preview-card">
            <div className="cs-preview-head">
              <h2>{tr('Live preview', 'معاينة مباشرة')}</h2>
              <span className="cs-hint">{previewing ? tr('Updating…', 'جارٍ التحديث…') : tr('Updates as you edit', 'يتحدّث أثناء التعديل')}</span>
            </div>
            <p className="cs-hint">
              {tr('Built by the same engine as the website: pinned products, then automatic ones, up to the homepage limit.', 'يُبنى بنفس محرك الموقع: المنتجات المثبّتة ثم التلقائية حتى حد الصفحة الرئيسية.')}
            </p>
            {preview ? <PreviewGrid preview={preview} isRtl={isRtl} compact /> : <div className="cs-empty">{previewing ? tr('Loading preview…', 'جارٍ تحميل المعاينة…') : tr('Preview unavailable until the form is valid.', 'المعاينة غير متاحة حتى يصبح النموذج صالحاً.')}</div>}
            {dirty ? <div className="cs-unsaved">{tr('Unsaved changes', 'تغييرات غير محفوظة')}</div> : null}
          </div>
        </aside>
      </form>
    </div>
  );
};

export default ContentSectionEdit;
